import { SegmentPayload } from '@dev/ee-api/ee-api-models';
import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useRouteMatch } from 'react-router';
import { safelyDecodeBase64Url } from '@dev/ee-tools';

import { ApplicationState } from '@/store';
import { GetContactFieldsStart } from '@/store/customFields/actions';
import { ListsLoadHistoryTrackedStartAction, ListsLoadStart } from '@/store/lists/actions';
import {
    SegmentAddStart,
    SegmentClearRuleEvaluationResult,
    SegmentDeselect,
    SegmentRuleEvaluate,
    SegmentsGetNamesStart,
    SegmentsLoadHistoryTrackedStartAction,
    SegmentUpdateEdited,
    SingleSegmentLoadStart,
} from '@/store/segments/actions';

export const useSegmentDeselect = (): void => {
    const dispatch = useDispatch();
    dispatch(SegmentDeselect());
};

export const useSegmentClearRuleEvaluationResult = (): void => {
    const dispatch = useDispatch();
    dispatch(SegmentClearRuleEvaluationResult());
};

export const useSegmentAddStart = (name: string, rule: string, trackHistory = false): void => {
    const dispatch = useDispatch();

    dispatch(SegmentAddStart(name, rule, trackHistory));
};

export const useSegmentsLoadHistoryTrackedStart = (includeHistory?: boolean, from?: string, to?: string): void => {
    const dispatch = useDispatch();
    dispatch(SegmentsLoadHistoryTrackedStartAction(includeHistory, from, to));
};

export const useSegmentRuleEvaluate = (rule: string): void => {
    const dispatch = useDispatch();
    dispatch(SegmentRuleEvaluate(rule));
};

export const useSegmentUpdateEdited = (name: string, segmentPayload: SegmentPayload): void => {
    const dispatch = useDispatch();
    dispatch(SegmentUpdateEdited(name, segmentPayload));
};

export const useSingleSegmentLoadStart = (name: string): void => {
    const dispatch = useDispatch();
    dispatch(SingleSegmentLoadStart(name));
};

export const useSegmentsGetNamesStart = (): void => {
    const dispatch = useDispatch();
    dispatch(SegmentsGetNamesStart());
};

export const useListsLoadHistoryTrackedStart = (includeHistory?: boolean, from?: string, to?: string): void => {
    const dispatch = useDispatch();
    dispatch(ListsLoadHistoryTrackedStartAction(includeHistory, from, to));
};

export const useListsLoadStart = (params?: {
    search?: string;
    from?: string;
    to?: string;
    limit?: number;
    offset?: number;
    includeHistory?: boolean;
    modifyQueryString?: boolean;
}): void => {
    const dispatch = useDispatch();
    dispatch(ListsLoadStart(params));
};

export const useGetContactFieldsStart = (): void => {
    const dispatch = useDispatch();
    dispatch(GetContactFieldsStart());
};

export const getDataFromStore = (state: ApplicationState): any => {
    const { account, lists, segments, customFields, config } = state;

    return { account, lists, segments, customFields, config };
};

export const useInitData = (): void => {
    const match = useRouteMatch();
    const dispatch = useDispatch();

    useEffect(() => {
        const { segmentname } = match.params as any;
        const isEditScreen = !!segmentname;
        const initialName = segmentname ? safelyDecodeBase64Url(segmentname) : '';

        dispatch(SegmentsLoadHistoryTrackedStartAction(false));
        dispatch(ListsLoadHistoryTrackedStartAction(false));
        dispatch(ListsLoadStart({ limit: 0, offset: 0, modifyQueryString: false }));
        dispatch(GetContactFieldsStart());
        dispatch(SegmentsGetNamesStart());

        if (isEditScreen) {
            dispatch(SingleSegmentLoadStart(initialName));
        }

        return (): void => {
            dispatch(SegmentClearRuleEvaluationResult());
            dispatch(SegmentDeselect());
        };
    }, []);
};
